import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { decamelizeKeys } from 'humps';
import uiNotifications from 'services/uiNotification.service';
import * as userResource from 'resources/user/hooks';
import { reorderTasks } from 'resources/task/hooks/useMilestoneTaskDnD/index.helpers';
import { isMilestoneNameValid, getMilestones } from './index.helpers';
import useSaveAndPublishByDiffs from './useSaveAndPublishByDiffs';
import styles from 'pages/tasks/styles.module.scss';

const useTempListedKitDataBeforeUpdate = ({ listedKit, attachedSignDocumentIds, defaultMilestones, defaultAudits }) => {
  const milestonesAndAuditsSnapshot = useRef();
  const tasksSnapshot = useRef();
  const attachedSignDocumentIdsSnapshot = useRef();

  const [milestonesAndAuditsForChanges, setMilestonesAndAuditsForChanges] = useState([]);
  const [tasksForChanges, setTasksForChanges] = useState([]);
  const [attachedSignDocumentIdsForChanges, setAttachedSignDocumentIdsForChanges] = useState([]);
  const [milestoneOrAuditIdToHighlight, setMilestoneOrAuditIdToHighlight] = useState(null);

  const { data: user } = userResource.useCurrentUser();

  // setup initial data
  useEffect(() => {
    if (listedKit || defaultMilestones) {
      const milestones = listedKit?.milestones || defaultMilestones;

      const milestonesWithoutTasks = milestones.map(({ tasks, ...restMilestoneInfo }) => restMilestoneInfo);
      const tasks = milestones.flatMap((milestone) => milestone.tasks);

      if (defaultAudits) {
        milestonesWithoutTasks.push(...defaultAudits);
      }

      milestonesAndAuditsSnapshot.current = milestonesWithoutTasks;
      tasksSnapshot.current = tasks;
      setMilestonesAndAuditsForChanges(milestonesWithoutTasks);
      setTasksForChanges(tasks);
    }

    attachedSignDocumentIdsSnapshot.current = attachedSignDocumentIds || [];
    setAttachedSignDocumentIdsForChanges(attachedSignDocumentIds || []);
  }, [defaultMilestones, defaultAudits, listedKit, attachedSignDocumentIds]);

  // the effect to scroll to a newly added milestone/audit and highlight it
  useEffect(() => {
    if (!milestoneOrAuditIdToHighlight) return;

    const milestoneOrAudit = milestonesAndAuditsForChanges.find(
      (milestoneOrAudit) => milestoneOrAudit.id === milestoneOrAuditIdToHighlight,
    );
    if (!milestoneOrAudit) return;

    const elementToHighlight = document.getElementById(milestoneOrAudit.id);
    if (!elementToHighlight) return;

    // Using a timeout to ensure this operation is not interrupted
    setTimeout(() => {
      // scroll to the milestone/audit
      elementToHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // add a highlight effect
      elementToHighlight.classList.add(styles.highlight);
      setTimeout(() => {
        elementToHighlight.classList.remove(styles.highlight);
        setMilestoneOrAuditIdToHighlight(null);
      }, 3 * 1000);
    }, 500);
  }, [milestonesAndAuditsForChanges, milestoneOrAuditIdToHighlight]);

  // data with changes for view
  const milestonesWithChanges = useMemo(() => {
    const milestonesWithTasks = [];

    milestonesAndAuditsForChanges.forEach((milestone) => {
      milestonesWithTasks.push({
        ...milestone,
        tasks: tasksForChanges.filter((task) => task.milestone_id === milestone.id),
      });
    });

    return milestonesWithTasks;
  }, [milestonesAndAuditsForChanges, tasksForChanges]);

  // update tasksForChanges in case of task change
  const updateTasks = useCallback(({ milestoneId, task: updatedTask }) => {
    // task was deleted
    if (updatedTask.isDelete) {
      const reorderedTasks = reorderTasks(tasksForChanges.filter((task) => (task.id !== updatedTask.id)
       && (task.milestone_id === milestoneId)));

      const oldTasks = tasksForChanges.filter((task) => !reorderedTasks.find((t) => t.id === task.id));

      setTasksForChanges([...oldTasks, ...reorderedTasks].filter((task) => task.id !== updatedTask.id));
      return;
    }

    // task was created
    if (updatedTask.isNew) {
      const newTask = {
        ...decamelizeKeys(updatedTask),
        id: uuidv4(),
        company_id: user.company_id,
        tempFiles: updatedTask.files,
        fileData: {
          files: updatedTask.files,
          uploaderId: user.id,
        },
      };

      // use updater function to make sure that many new tasks can be added
      setTasksForChanges((prevTasks) => {
        const milestoneTasks = prevTasks.filter((task) => task.milestone_id === milestoneId);
        const milestoneTasksWithNew = [...milestoneTasks, newTask];
        const milestoneTasksWithNewOrdered = reorderTasks(milestoneTasksWithNew);
    
        const otherMilestonesTasks = prevTasks.filter((task) => !milestoneTasksWithNewOrdered.find((t) => t.id === task.id));
    
        return [...otherMilestonesTasks, ...milestoneTasksWithNewOrdered];
      });

      return;
    }

    setTasksForChanges(
      tasksForChanges.map((task) => (task.id === updatedTask.id
        ? {
          ...task,
          ...decamelizeKeys(updatedTask),

          taskCompanyId: user.company_id, // used to upload document in 'updateTaskWithDocument' method
          tempFiles: updatedTask.files,
          fileData: {
            files: updatedTask.files,
            uploaderId: user.id,
          },
        }
        : task)),
    );
  }, [tasksForChanges, user?.company_id, user?.id]);

  // occurs when tasks within milestone changed, or milestone itself
  const handleMilestoneOrAuditUpdate = useCallback((updatedMilestoneData) => {
    const milestoneToUpdate = milestonesAndAuditsForChanges.find(
      (milestone) => milestone.id === updatedMilestoneData.id,
    );

    // * in case of somethings happens with task
    if (updatedMilestoneData.task) {
      updatedMilestoneData.task.milestone_id = updatedMilestoneData.task.milestoneId || milestoneToUpdate.id;
      // updatedMilestoneData.task.milestoneId = updatedMilestoneData.task.milestoneId || milestoneToUpdate.id;

      updateTasks({
        milestoneId: updatedMilestoneData.id,
        task: updatedMilestoneData.task,
      });
      return;
    }

    // * in case of milestone changes
    const isNameValid = milestoneToUpdate.isAudit || isMilestoneNameValid({
      milestonesToCheck: getMilestones(milestonesAndAuditsForChanges).filter((m) => m.id !== milestoneToUpdate.id),
      milestoneName: updatedMilestoneData.name,
    });

    if (!isNameValid) {
      uiNotifications.showError(`Milestone with the name "${updatedMilestoneData.name}" already exists. Please specify a unique name.`);
      return;
    }

    setMilestonesAndAuditsForChanges(milestonesAndAuditsForChanges.map((milestone) => {
      if (milestone.id === updatedMilestoneData.id) {
        return ({ ...milestoneToUpdate, ...updatedMilestoneData });
      }
      return milestone;
    }));
  }, [milestonesAndAuditsForChanges, updateTasks]);

  const handleMilestoneOrAuditAdd = useCallback((newMilestoneData) => {
    const isNameValid = newMilestoneData.isAudit || isMilestoneNameValid({
      milestonesToCheck: getMilestones(milestonesAndAuditsForChanges),
      milestoneName: newMilestoneData.name,
    });

    if (!isNameValid) {
      uiNotifications.showError(`Milestone with the name "${newMilestoneData.name}" already exists. Please specify a unique name.`);
      return false;
    }

    setMilestonesAndAuditsForChanges([...milestonesAndAuditsForChanges, newMilestoneData]);

    // used to scroll to the last added milestone/audit
    setMilestoneOrAuditIdToHighlight(newMilestoneData.id);

    return true;
  }, [milestonesAndAuditsForChanges]);

  const handleDocumentsToSignAdd = useCallback(({ documentIds: newDocumentsData }) => {
    setAttachedSignDocumentIdsForChanges([...attachedSignDocumentIdsForChanges, ...newDocumentsData]);
  }, [attachedSignDocumentIdsForChanges]);

  const handleDocumentsToSignDelete = useCallback((deletedDocumentId) => {
    setAttachedSignDocumentIdsForChanges(attachedSignDocumentIdsForChanges.filter(
      (documentId) => documentId !== deletedDocumentId,
    ));
  }, [attachedSignDocumentIdsForChanges]);

  const handleMilestoneOrAuditDelete = useCallback((deletedMilestoneId) => {
    // update milestone list
    setMilestonesAndAuditsForChanges(milestonesAndAuditsForChanges.filter(
      (milestone) => milestone.id !== deletedMilestoneId,
    ));

    // remove all tasks related to deleted milestone
    setTasksForChanges(tasksForChanges.filter((task) => task.milestone_id !== deletedMilestoneId));
  }, [milestonesAndAuditsForChanges, tasksForChanges]);

  const handleReorderMilestoneTasks = useCallback((milestonesToReorder) => {
    const reorderedTasks = milestonesToReorder.flatMap((m) => m.tasks);
    const oldTasks = tasksForChanges.filter((task) => !reorderedTasks.find((t) => t.id === task.id));

    setTasksForChanges([...oldTasks, ...reorderedTasks]);
  }, [tasksForChanges]);

  const {
    isListedKitDataProcessing,
    handleSaveAndPublishClick,
    prepareDataForSaveAndPublish,
  } = useSaveAndPublishByDiffs({
    listedKit,
    defaultMilestones,
    milestonesAndAuditsSnapshot,
    tasksSnapshot,
    attachedSignDocumentIdsSnapshot,
    milestonesAndAuditsForChanges,
    tasksForChanges,
    milestonesWithChanges,
    attachedSignDocumentIdsForChanges,
  });

  return {
    isListedKitDataProcessing,
    milestonesWithChanges,
    attachedSignDocumentIdsForChanges,
    handleMilestoneOrAuditAdd,
    handleMilestoneOrAuditUpdate,
    handleMilestoneOrAuditDelete,
    handleDocumentsToSignAdd,
    handleDocumentsToSignDelete,
    handleSaveAndPublishClick,
    handleReorderMilestoneTasks,
    prepareDataForSaveAndPublish,
    updateTasks,
  };
};

export default useTempListedKitDataBeforeUpdate;
